import type { ParcelRow } from '@mollusk/shared';
import Box from '@mui/material/Box';
import Typography from '@mui/material/Typography';
import { useQuery } from '@tanstack/react-query';
import mapboxgl from 'mapbox-gl';
import { useEffect, useMemo, useRef, useState } from 'react';
import { ApiError } from '../../api/client';
import { api } from '../../api/endpoints';
import { LoadingPill } from '../../components/LoadingPill';
import { CoverageLegend } from '../coverage/CoverageLegend';
import { byFeatureId, coverageByStateFips, paintFeatures } from '../coverage/geo';
import { EMPTY_FC, setSourceData } from '../coverage/mapUtil';
import { topoToFeatures } from '../coverage/topo';
import { ParcelDetailModal } from '../parcels/ParcelDetailModal';
import { wktToGeoJSON } from '../parcels/wkt';
import { PARCEL_ZOOM_THRESHOLD, layerVisibility, parcelMapMode } from './mode';

mapboxgl.accessToken = import.meta.env.VITE_MAPBOX_PUBLIC_TOKEN ?? '';

const US_CENTER: [number, number] = [-98, 38];
const US_ZOOM = 4;

const STATES_FILL = 'pm-states-fill';
const STATES_LINE = 'pm-states-line';
const COUNTIES_FILL = 'pm-counties-fill';
const COUNTIES_LINE = 'pm-counties-line';
const PARCELS_SRC = 'pm-parcels';
const PARCELS_FILL = 'pm-parcels-fill';
const PARCELS_LINE = 'pm-parcels-line';

/**
 * Flat (Mercator) US parcel map. Below the parcel zoom threshold it shows the
 * parcel-coverage choropleth (states, then counties); at/above it, it fetches
 * and draws individual parcel footprints in the viewport and lets you click one
 * for detail.
 */
export function ParcelMapView() {
  const containerRef = useRef<HTMLDivElement>(null);
  const mapRef = useRef<mapboxgl.Map | null>(null);
  const [zoom, setZoom] = useState(US_ZOOM);
  const [bbox, setBbox] = useState<[number, number, number, number] | null>(null);
  const [active, setActive] = useState<ParcelRow[]>([]);

  const mode = parcelMapMode(zoom);

  const summary = useQuery({ queryKey: ['coverage-parcels'], queryFn: api.coverageParcels });
  const statesGeo = useQuery({
    queryKey: ['coverage-geo', 'states'],
    queryFn: () => api.coverageGeo('states'),
    staleTime: Number.POSITIVE_INFINITY,
  });
  const countiesGeo = useQuery({
    queryKey: ['coverage-geo', 'counties'],
    queryFn: () => api.coverageGeo('counties'),
    staleTime: Number.POSITIVE_INFINITY,
  });

  const stateFeatures = useMemo(
    () => (statesGeo.data ? topoToFeatures(statesGeo.data, 'states') : []),
    [statesGeo.data],
  );
  const countyFeatures = useMemo(
    () => (countiesGeo.data ? topoToFeatures(countiesGeo.data, 'counties') : []),
    [countiesGeo.data],
  );

  const statesFC = useMemo(() => {
    if (!summary.data || stateFeatures.length === 0) return null;
    return paintFeatures(stateFeatures, byFeatureId, coverageByStateFips(summary.data.states));
  }, [summary.data, stateFeatures]);

  const countyCoverage = useMemo(() => {
    const m = new Map<string, number>();
    for (const c of summary.data?.counties ?? []) m.set(c.county_fips, 1);
    return m;
  }, [summary.data]);

  const countiesFC = useMemo(() => {
    if (!summary.data || countyFeatures.length === 0) return null;
    return paintFeatures(countyFeatures, byFeatureId, countyCoverage);
  }, [summary.data, countyFeatures, countyCoverage]);

  // Only fetch parcels once we're zoomed in far enough to draw them — at low
  // zoom a bbox spans far more than the 500-parcel cap and isn't shown anyway.
  const parcelsQuery = useQuery({
    queryKey: ['parcels', bbox],
    enabled: mode === 'parcels' && !!bbox,
    queryFn: () => {
      if (!bbox) throw new Error('no bbox');
      return api.parcels(bbox);
    },
  });

  // Initialise the map once (flat projection).
  useEffect(() => {
    if (!containerRef.current || mapRef.current) return;
    const map = new mapboxgl.Map({
      container: containerRef.current,
      style: 'mapbox://styles/mapbox/light-v11',
      projection: 'mercator',
      center: US_CENTER,
      zoom: US_ZOOM,
    });
    mapRef.current = map;
    map.on('load', () => {
      map.addSource(STATES_FILL, { type: 'geojson', data: EMPTY_FC });
      map.addSource(COUNTIES_FILL, { type: 'geojson', data: EMPTY_FC });
      map.addSource(PARCELS_SRC, { type: 'geojson', data: EMPTY_FC });
      map.addLayer({
        id: STATES_FILL,
        type: 'fill',
        source: STATES_FILL,
        paint: { 'fill-color': ['get', 'color'], 'fill-opacity': 0.75 },
      });
      map.addLayer({
        id: STATES_LINE,
        type: 'line',
        source: STATES_FILL,
        paint: { 'line-color': '#ffffff', 'line-width': 1 },
      });
      map.addLayer({
        id: COUNTIES_FILL,
        type: 'fill',
        source: COUNTIES_FILL,
        layout: { visibility: 'none' },
        paint: { 'fill-color': ['get', 'color'], 'fill-opacity': 0.7 },
      });
      map.addLayer({
        id: COUNTIES_LINE,
        type: 'line',
        source: COUNTIES_FILL,
        layout: { visibility: 'none' },
        paint: { 'line-color': '#022b3a', 'line-width': 0.4, 'line-opacity': 0.35 },
      });
      map.addLayer({
        id: PARCELS_FILL,
        type: 'fill',
        source: PARCELS_SRC,
        layout: { visibility: 'none' },
        paint: { 'fill-color': '#1f7a8c', 'fill-opacity': 0.35 },
      });
      map.addLayer({
        id: PARCELS_LINE,
        type: 'line',
        source: PARCELS_SRC,
        layout: { visibility: 'none' },
        paint: { 'line-color': '#022b3a', 'line-width': 1, 'line-opacity': 0.6 },
      });

      const sync = () => {
        setZoom(map.getZoom());
        const b = map.getBounds();
        if (b) setBbox([b.getWest(), b.getSouth(), b.getEast(), b.getNorth()]);
      };
      sync();
      map.on('moveend', sync);

      map.on('click', PARCELS_FILL, (e) => {
        const rows: ParcelRow[] = [];
        for (const f of e.features ?? []) {
          const raw = f.properties?.__row;
          if (typeof raw !== 'string') continue;
          try {
            rows.push(JSON.parse(raw) as ParcelRow);
          } catch {
            // ignore unparseable row
          }
        }
        const deduped = Array.from(new Map(rows.map((r) => [r.parcel_id, r])).values());
        if (deduped.length) setActive(deduped);
      });
      map.on('mouseenter', PARCELS_FILL, () => {
        map.getCanvas().style.cursor = 'pointer';
      });
      map.on('mouseleave', PARCELS_FILL, () => {
        map.getCanvas().style.cursor = '';
      });
    });
    return () => {
      map.remove();
      mapRef.current = null;
    };
  }, []);

  // Push painted choropleth data into the sources.
  useEffect(() => {
    if (mapRef.current && statesFC) setSourceData(mapRef.current, STATES_FILL, statesFC);
  }, [statesFC]);
  useEffect(() => {
    if (mapRef.current && countiesFC) setSourceData(mapRef.current, COUNTIES_FILL, countiesFC);
  }, [countiesFC]);

  // Toggle layer visibility as the zoom mode changes.
  useEffect(() => {
    const map = mapRef.current;
    if (!map) return;
    const vis = layerVisibility(mode);
    const set = (id: string, on: boolean) => {
      if (map.getLayer(id)) map.setLayoutProperty(id, 'visibility', on ? 'visible' : 'none');
    };
    const apply = () => {
      set(STATES_FILL, vis.states);
      set(STATES_LINE, vis.states);
      set(COUNTIES_FILL, vis.counties);
      set(COUNTIES_LINE, vis.counties);
      set(PARCELS_FILL, vis.parcels);
      set(PARCELS_LINE, vis.parcels);
    };
    // Guard on layer existence, not isStyleLoaded(): after the initial load the
    // layers persist, but isStyleLoaded() goes false whenever tiles are
    // fetching (e.g. mid-zoom) — and `once('load')` would never fire again, so
    // a mode change during a zoom would be silently dropped and the choropleth
    // would stay painted over the parcels.
    if (map.getLayer(PARCELS_FILL)) apply();
    else map.once('load', apply);
  }, [mode]);

  // Draw fetched parcels into the parcels source.
  useEffect(() => {
    const map = mapRef.current;
    const data = parcelsQuery.data;
    if (!map || !data) return;
    const apply = () => {
      const src = map.getSource(PARCELS_SRC) as mapboxgl.GeoJSONSource | undefined;
      if (!src) return;
      const features: GeoJSON.Feature[] = data.parcels.flatMap((p) => {
        const geom = p.geometry ? wktToGeoJSON(p.geometry) : null;
        if (!geom) return [];
        return [{ type: 'Feature', geometry: geom, properties: { __row: JSON.stringify(p) } }];
      });
      src.setData({ type: 'FeatureCollection', features });
    };
    if (map.getSource(PARCELS_SRC)) apply();
    else map.once('load', apply);
    return () => {
      map.off('load', apply);
    };
  }, [parcelsQuery.data]);

  const error = summary.error ?? statesGeo.error ?? countiesGeo.error ?? parcelsQuery.error;
  const loading =
    summary.isFetching || statesGeo.isFetching || countiesGeo.isFetching || parcelsQuery.isFetching;

  return (
    <Box>
      {error instanceof ApiError ? <Typography color="error">{error.message}</Typography> : null}
      <Typography variant="body2" color="text.secondary" sx={{ mb: 1, minHeight: 40 }}>
        {mode === 'parcels'
          ? 'Showing individual parcels in view (up to 500). Click one for its detail.'
          : `Shading = where we hold parcel data (${mode === 'states' ? 'by state' : 'by county'}). Zoom in to see parcels.`}
      </Typography>
      <Box sx={{ position: 'relative', width: '100%', height: 560 }}>
        <Box
          ref={containerRef}
          sx={{ position: 'absolute', inset: 0, borderRadius: 1, overflow: 'hidden' }}
        />
        {mode !== 'parcels' ? <CoverageLegend unit="parcels" /> : null}
        {loading ? (
          <LoadingPill label="Loading…" sx={{ position: 'absolute', top: 12, right: 12 }} />
        ) : null}
      </Box>
      <ParcelDetailModal parcels={active} onClose={() => setActive([])} />
    </Box>
  );
}
